import IAppLogger from '@app/lib/IAppLogger';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import { STORAGE_KEY_AUTH_RESPONSE } from '../../../constants/storage';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationRequestHandler,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  BasicQueryStringUtils,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  LocalStorageBackend,
  LocationLike,
  RedirectRequestHandler,
  StringMap,
  TokenRequest,
  TokenRequestHandler,
  TokenResponse,
} from '@openid/appauth';

type AuthorizationCodeResponse = {
  code: string;
  codeVerifier?: string;
};

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse(input: LocationLike) {
    return super.parse(input, false);
  }
}

/**
 * The `AuthFlow` class handles the authentication flow for the application.
 * It manages the authorization and token requests, handles the authentication state,
 * and provides methods to initiate and complete the authorization process, refresh tokens,
 * and check the authentication status.
 *
 * @class
 * @classdesc This class is responsible for managing the OAuth2/OpenID Connect authentication flow.
 *
 * @example
 * ```typescript
 * const authFlow = new AuthFlow(
 *   'https://issuer.example.com',
 *   'client-id',
 *   ['openid', 'profile'],
 *   '/login-redirect',
 *   '/logout-redirect',
 *   logger,
 *   (authenticated) => {
 *     console.log('Authentication state changed:', authenticated);
 *   }
 * );
 *
 * // Initiate authorization
 * authFlow.requestAuthorization();
 *
 * // Complete authorization
 * authFlow.completeAuthorization();
 *
 * // Check if signed in
 * const isSignedIn = authFlow.signedIn();
 *
 * // Refresh tokens
 * authFlow.refreshTokens();
 *
 * // Request end session
 * authFlow.requestEndSession();
 * ```
 *
 * @param {string} issuerUrl - The URL of the authorization server's issuer.
 * @param {string} clientId - The client ID for the application.
 * @param {string[]} scopes - An array of scopes for the authorization request.
 * @param {string} loginRedirectPath - The path to redirect to after login.
 * @param {string} logoutRedirectPath - The path to redirect to after logout.
 * @param {IAppLogger} logger - An instance of the application logger.
 * @param {(authenticated: boolean) => void} onStateChanged - A callback function that is called when the authentication state changes.
 */
export class AuthFlow {
  private issuerUrl: string;
  private clientId: string;
  private scopes: string[];
  private loginRedirectUri: string;
  private logoutRedirectUri: string;
  private logger: IAppLogger;
  private notifier: AuthorizationNotifier;
  private requestor = new FetchRequestor();
  private authorizationHandler: AuthorizationRequestHandler;
  private tokenHandler: TokenRequestHandler;
  private configuration: AuthorizationServiceConfiguration | undefined;
  private accessTokenResponse: TokenResponse | undefined;
  private authorizationCodeResponse: AuthorizationCodeResponse | undefined;
  private onStateChanged: (authenticated: boolean) => void;

  /**
   * Constructs an instance of the AuthFlow class.
   *
   * @param issuerUrl - The URL of the authorization server's issuer.
   * @param clientId - The client ID for the application.
   * @param scopes - An array of scopes for the authorization request.
   * @param loginRedirectPath - The path to redirect to after login.
   * @param logoutRedirectPath - The path to redirect to after logout.
   * @param logger - An instance of the application logger.
   * @param onStateChanged - A callback function that is called when the authentication state changes.
   */
  constructor(
    issuerUrl: string,
    clientId: string,
    scopes: string[],
    loginRedirectPath: string,
    logoutRedirectPath: string,
    logger: IAppLogger,
    onStateChanged: (authenticated: boolean) => void,
  ) {
    this.issuerUrl = issuerUrl;
    this.clientId = clientId;
    this.scopes = scopes;
    this.loginRedirectUri = this.createRedirectUri(loginRedirectPath);
    this.logoutRedirectUri = this.createRedirectUri(logoutRedirectPath);
    this.logger = logger;
    this.onStateChanged = onStateChanged;
    this.notifier = new AuthorizationNotifier();
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    this.tokenHandler = new BaseTokenRequestHandler(this.requestor);
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    this.notifier.setAuthorizationListener((request, response, error) => {
      this.logger.debug(
        'authorization request complete ',
        request,
        response,
        error,
      );
      if (response) {
        this.authorizationCodeResponse = {
          code: response.code,
          codeVerifier: request.internal?.code_verifier,
        };
      }
    });
    this.readState();
  }

  /**
   * Retrieves the access token from the access token response.
   *
   * @returns {string | undefined} The access token if available, otherwise undefined.
   */
  public get accessToken(): string | undefined {
    return this.accessTokenResponse?.accessToken;
  }

  /**
   * Initiates an authorization request using the provided username and identity provider (idp).
   *
   * @param username - Optional. The username to be used as a login hint.
   * @param idp - Optional. The identity provider to be used for the authorization request.
   * @returns A promise that resolves when the authorization request is completed.
   * @throws Will throw an error if the service configuration is unknown.
   */
  public async requestAuthorization(
    username?: string,
    idp?: string,
  ): Promise<void> {
    await this.fetchServiceConfiguration();
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      throw new Error('Unknown service configuration');
    }

    const extras: StringMap = { access_type: 'offline' };
    if (username) {
      extras['login_hint'] = username;
    }
    if (idp) {
      extras['idp'] = idp;
    }

    // create a request
    const request = new AuthorizationRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      scope: this.createScope(this.scopes),
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: undefined,
      extras: extras,
    });

    this.logger.debug(
      'making authorization request ',
      this.configuration,
      request,
    );

    this.authorizationHandler.performAuthorizationRequest(
      this.configuration,
      request,
    );
  }

  /**
   * Completes the authorization process by fetching the service configuration,
   * handling the authorization request, and making a token request with the
   * authorization code. If the service configuration is not available, an error
   * is logged and thrown.
   *
   * @throws {Error} If the service configuration is unknown.
   *
   * @returns {Promise<boolean>} `true` if authorization completed successfully.
   */
  public async completeAuthorization(): Promise<boolean> {
    await this.fetchServiceConfiguration();
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      throw new Error('Unknown service configuration');
    }

    await this.authorizationHandler.completeAuthorizationRequestIfPossible();
    if (this.authorizationCodeResponse) {
      await this.makeTokenRequestWithCode(
        this.authorizationCodeResponse.code,
        this.authorizationCodeResponse.codeVerifier,
      );

      // COMMENTED OUT: Redundant refresh call after authorization
      // - makeTokenRequestWithCode() just fetched fresh tokens from IUM
      // - refreshTokens() checks isValid() first, which will always be true for fresh tokens
      // - This call effectively becomes a no-op (returns immediately without server call)
      // - Removing this eliminates unnecessary code and potential failure point during login
      // - If refresh is ever needed here, use AuthModule.refreshTokensWithRetry() for proper error handling
      // await this.refreshTokens();

      this.logger.debug('complete');
      return true;
    }

    return false;
  }

  /**
   * Checks if the user is signed in by verifying the presence of an access token response,
   * an access token, and a refresh token.
   *
   * @returns {boolean} `true` if the user is signed in, otherwise `false`.
   */
  public signedIn(): boolean {
    return (
      !!this.accessTokenResponse &&
      !!this.accessTokenResponse.accessToken &&
      !!this.accessTokenResponse.refreshToken
    );
  }

  /**
   * Checks if the current access token is valid within a specified buffer time.
   *
   * @param buffer - The buffer time in seconds to check the token validity.
   * @returns `true` if the access token is valid within the buffer time, otherwise `false`.
   */
  public isTokenValid(buffer: number): boolean {
    if (
      !!this.accessTokenResponse &&
      !!this.accessTokenResponse.accessToken &&
      !!this.accessTokenResponse.refreshToken
    ) {
      return this.accessTokenResponse.isValid(buffer);
    }
    return false;
  }

  /**
   * Initiates the end session process by redirecting the user to the end-session endpoint.
   *
   * This method validates the required parameters, constructs the end-session URL using the
   * issuer URL, post-logout redirect URI, and ID token hint. It then clears the current
   * authentication state and redirects the user to the constructed URL to complete the logout process.
   *
   * @remarks
   * - Validates that issuerUrl and logoutRedirectUri are present before redirecting
   * - Logs the logout attempt with full context for debugging
   * - Always clears local state and redirects to IDP, even if storage cleanup fails
   * - The `post_logout_redirect_uri` is URL-encoded to ensure it is properly formatted for the request.
   * - IDP logout is prioritized over local storage cleanup for security
   * - If validation fails, logs an error and throws to allow caller to handle gracefully
   *
   * @throws If required parameters (issuerUrl, logoutRedirectUri) are missing
   */
  public requestEndSession(): void {
    // Validate required parameters before attempting redirect
    if (!this.issuerUrl) {
      const error = new Error(
        'Cannot request end session: issuerUrl is not configured',
      );
      this.logger.error('End session request failed - missing issuerUrl', {
        issuerUrl: this.issuerUrl,
        logoutRedirectUri: this.logoutRedirectUri,
        hasIdToken: !!this.accessTokenResponse?.idToken,
      });
      throw error;
    }

    if (!this.logoutRedirectUri) {
      const error = new Error(
        'Cannot request end session: logoutRedirectUri is not configured',
      );
      this.logger.error(
        'End session request failed - missing logoutRedirectUri',
        {
          issuerUrl: this.issuerUrl,
          logoutRedirectUri: this.logoutRedirectUri,
          hasIdToken: !!this.accessTokenResponse?.idToken,
        },
      );
      throw error;
    }

    // Construct the end-session URL
    const idToken = this.accessTokenResponse?.idToken;
    const endSessionUrl = `${this.issuerUrl}/connect/endsession?post_logout_redirect_uri=${encodeURIComponent(this.logoutRedirectUri)}&id_token_hint=${idToken ?? ''}`;

    // Log the logout attempt with context for debugging
    this.logger.info('Requesting end session', {
      issuerUrl: this.issuerUrl,
      logoutRedirectUri: this.logoutRedirectUri,
      hasIdToken: !!idToken,
      endSessionUrl: endSessionUrl.substring(0, 100) + '...', // Log truncated URL for privacy
    });

    // Clear our local state before redirect
    // Note: clear() is now resilient and will never throw, even if storage operations fail
    this.clear();

    // Redirect to the end-session endpoint to terminate the IDP session
    // This is the critical operation - even if local storage cleanup failed above,
    // the IDP logout ensures the user is properly logged out
    window.location.href = endSessionUrl;

    this.logger.info('End session redirect initiated successfully');
  }

  /**
   * Clears the cached token state.
   *
   * This method resets the `accessTokenResponse` to `undefined` and writes the updated state.
   */
  public clear() {
    // forget all cached token state
    this.accessTokenResponse = undefined;
    this.writeState();
  }

  /**
   * Refreshes the authentication tokens.
   *
   * This method fetches the service configuration and attempts to refresh the
   * authentication tokens using the refresh token. If the service configuration
   * is unknown or the refresh token is missing, appropriate error messages are
   * logged and the method returns a rejected promise or a resolved promise with
   * a message, respectively.
   *
   * If the current access token is still valid, the method resolves with the
   * current access token without performing any refresh operation.
   *
   * @returns {Promise<string>} A promise that resolves with the new access token
   * if the refresh operation is successful, or a message indicating the issue
   * if it fails.
   *
   * @throws {Error} If the service configuration is unknown or token refresh fails.
   * The error includes additional context about the failure type (network, auth, server).
   */
  public async refreshTokens(): Promise<string> {
    await this.fetchServiceConfiguration();
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      return Promise.reject(new Error('Unknown service configuration'));
    }

    if (!this.accessTokenResponse?.refreshToken) {
      this.logger.warn('Missing refreshToken.');
      return Promise.resolve('Missing refreshToken.');
    }

    if (this.accessTokenResponse?.isValid()) {
      // Token still valid, no refresh needed
      return Promise.resolve(this.accessTokenResponse.accessToken);
    }

    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      code: undefined,
      refresh_token: this.accessTokenResponse.refreshToken,
      extras: undefined,
    });

    try {
      const response = await this.tokenHandler.performTokenRequest(
        this.configuration,
        request,
      );
      this.accessTokenResponse = response;
      this.writeState();
      return response.accessToken;
    } catch (error: any) {
      // Enhance error with context for better debugging and retry decisions
      const errorMessage = error?.message ?? 'Unknown error';
      const errorCode = error?.error ?? 'unknown_error';

      this.logger.error('Token refresh request failed', {
        error: errorMessage,
        errorCode,
        issuerUrl: this.issuerUrl,
      });

      // Create enhanced error with context
      const enhancedError: any = new Error(
        `Token refresh failed: ${errorMessage}`,
      );
      enhancedError.originalError = error;
      enhancedError.errorCode = errorCode;
      enhancedError.issuerUrl = this.issuerUrl;

      // Add error type classification for intelligent retry logic
      if (
        errorMessage.includes('network') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('ECONNREFUSED')
      ) {
        enhancedError.errorType = 'network';
      } else if (
        errorCode === 'invalid_grant' ||
        errorMessage.includes('401') ||
        errorMessage.includes('unauthorized')
      ) {
        enhancedError.errorType = 'auth';
      } else if (
        errorMessage.includes('500') ||
        errorMessage.includes('503') ||
        errorMessage.includes('server error')
      ) {
        enhancedError.errorType = 'server';
      } else {
        enhancedError.errorType = 'unknown';
      }

      throw enhancedError;
    }
  }

  /**
   * Fetch the service configuration from the issuer
   */
  private async fetchServiceConfiguration(): Promise<void> {
    const response = await AuthorizationServiceConfiguration.fetchFromIssuer(
      this.issuerUrl,
      this.requestor,
    );
    this.logger.debug('fetched service configuration', response);
    this.configuration = response;
  }

  /**
   * Makes a token request using the provided authorization code and code verifier.
   *
   * @param code - The authorization code received from the authorization server.
   * @param codeVerifier - The code verifier used in the PKCE flow, if applicable.
   * @returns A promise that resolves to a `TokenResponse` object if the request is successful, or `undefined` if the configuration is missing.
   *
   * @throws Will log an error if the service configuration is unknown.
   */
  private async makeTokenRequestWithCode(
    code: string,
    codeVerifier: string | undefined,
  ): Promise<TokenResponse | undefined> {
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      return Promise.resolve(undefined);
    }

    const extras: StringMap = {};

    if (codeVerifier) {
      extras.code_verifier = codeVerifier;
    }

    // use the code to make the token request.
    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: code,
      refresh_token: undefined,
      extras: extras,
    });

    const response = await this.tokenHandler.performTokenRequest(
      this.configuration,
      request,
    );
    this.accessTokenResponse = response;
    this.writeState();
    return response;
  }

  /**
   * Make sure scope includes offline_access and profile
   * @param scope
   * @returns Final scopes to request
   */
  private createScope(scope: string[]): string {
    const allScopes = [...scope];

    if (!allScopes.includes('offline_access')) {
      allScopes.push('offline_access');
    }

    if (!allScopes.includes('profile')) {
      allScopes.push('profile');
    }

    return allScopes.join(' ');
  }

  private createRedirectUri(redirectPath: string): string {
    return `${window.location.origin}${redirectPath}`;
  }

  /**
   * Write the current auth state to local storage
   *
   * This method is resilient and will never throw errors. If storage operations
   * fail (e.g., privacy settings, quota exceeded), errors are logged but execution
   * continues. This ensures critical operations like logout can proceed even if
   * local storage cleanup fails.
   */
  private writeState(): void {
    try {
      // we need to track if the state has changed, gone from empty to a value
      // or from a value to empty (ignore the contents of the state)
      const prevState = sessionStorage.getItem(STORAGE_KEY_AUTH_RESPONSE);
      const newState = this.accessTokenResponse
        ? JSON.stringify(this.accessTokenResponse.toJson())
        : '';
      const hasPrevState = prevState !== null && prevState !== '';
      const hasNewState = newState !== null && newState !== '';
      const hasStateChanged = hasPrevState !== hasNewState;

      // write new state to local storage
      sessionStorage.setItem(STORAGE_KEY_AUTH_RESPONSE, newState);

      // if state changed, let our listeners know
      if (hasStateChanged) {
        this.onStateChanged(!!this.accessTokenResponse);
      }
    } catch (error: unknown) {
      // Storage operations can fail due to privacy settings, quota limits, or
      // browser restrictions. Log the error but don't throw - this allows
      // critical operations (like IDP logout) to proceed even if local cleanup fails.
      const errorMessage = extractErrorMessage(error);
      this.logger.warn('Failed to write auth state to storage', {
        error: errorMessage,
        hasAccessToken: !!this.accessTokenResponse,
      });
      // Note: In-memory state (this.accessTokenResponse) remains the source of truth
      // even if storage sync fails, so the app continues to function correctly.
    }
  }

  /**
   * Read the current auth state from local storage
   *
   * This method is resilient and will never throw errors. If storage operations
   * fail or the stored data is corrupted, the auth state is reset to undefined
   * and errors are logged.
   */
  private readState(): void {
    try {
      const state = sessionStorage.getItem(STORAGE_KEY_AUTH_RESPONSE);
      if (state === '' || state === null) {
        this.accessTokenResponse = undefined;
      } else {
        this.accessTokenResponse = new TokenResponse(JSON.parse(state));
      }
    } catch (error: unknown) {
      // Storage read can fail due to privacy settings, corrupted data, or browser restrictions.
      // Reset to unauthenticated state and log the error.
      const errorMessage = extractErrorMessage(error);
      this.logger.warn('Failed to read auth state from storage', {
        error: errorMessage,
      });
      this.accessTokenResponse = undefined;
    }
  }
}
